/**
 * Registration, beside the open research — Or's card.
 *
 * Same rules as `EnrollStep`: Q1's single approved checkbox, a real
 * `<input type="checkbox">`, and nothing submitted without both a valid
 * address and consent.
 */

import React, { useEffect, useRef, useState } from 'react';
import { copyFor, SHARED } from '../audienceCopy';

const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || '').trim());

export default function MockEnroll({ audience, onEnroll, submitting, error }) {
  const copy = copyFor(audience);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [consent, setConsent] = useState(false);
  const [touched, setTouched] = useState(false);
  const nameRef = useRef(null);
  const emailRef = useRef(null);
  const consentRef = useRef(null);

  // The caret waits in the first field. This card appears beside a report
  // the visitor has just opened, so the keyboard has nowhere useful to be
  // otherwise, and reaching the form means tabbing past the whole editor.
  useEffect(() => { nameRef.current?.focus(); }, []);

  // What is still missing, or null when the form is ready. Drives both the
  // button's appearance and the message it gives when pressed early.
  const missing = isEmail(email)
    ? (consent ? null : SHARED.consentRequired)
    : (consent ? SHARED.emailRequired : SHARED.emailAndConsentRequired);

  const submit = () => {
    setTouched(true);
    if (submitting) return;
    if (missing) {
      // Send them to the thing that is missing rather than leaving them to
      // find it. The box sits under two filled fields and is easy to miss.
      if (!consent) consentRef.current?.focus();
      else emailRef.current?.focus();
      return;
    }
    onEnroll({ email: email.trim(), name: name.trim() || undefined, consent: true });
  };

  return (
    <aside className="mk-card mk-enroll">
      <h2 className="mk-h2">{copy.invitation}</h2>
      {copy.enrollmentDetail.split('\n\n').map((para) => (
        <p className="mk-body" key={para.slice(0, 32)}>{para}</p>
      ))}

      <label className="mk-label" htmlFor="mk-name">
        Name <span style={{ fontWeight: 400, color: 'var(--mk-muted)' }}>Optional</span>
      </label>
      <input
        id="mk-name"
        ref={nameRef}
        className="mk-input"
        value={name}
        onChange={(e) => setName(e.target.value)}
        style={{ marginBottom: 14 }}
      />

      <label className="mk-label" htmlFor="mk-email">Email address</label>
      <input
        id="mk-email"
        ref={emailRef}
        className="mk-input"
        type="email"
        placeholder="you@example.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />

      <div className="mk-choices">
        <label className="mk-choice">
          <input
            type="checkbox"
            ref={consentRef}
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
          />
          {copy.registrationCheckbox}
        </label>
      </div>

      {touched && missing && <div className="mk-error">{missing}</div>}
      {error && <div className="mk-error">{error}</div>}

      <div className="mk-actions">
        {/* `aria-disabled`, not `disabled`. The button has to stay
            clickable so that pressing it can say what is missing; a truly
            disabled control swallows the click and explains nothing, which
            is how people end up staring at a form that will not submit.
            The grey tells them it is not ready yet. */}
        <button
          type="button"
          className={`mk-btn mk-btn--primary mk-btn--block${missing ? ' mk-btn--inactive' : ''}`}
          onClick={submit}
          disabled={submitting}
          aria-disabled={missing ? 'true' : undefined}
        >
          Get my opportunities
        </button>
      </div>
      {SHARED.enrollFootnote.map((line) => (
        <p className="mk-note" key={line.slice(0, 24)}>{line}</p>
      ))}
    </aside>
  );
}
